import Service, { service } from '@ember/service';

import { action } from '@ember/object';
import { getOwner } from '@ember/owner';
import { tracked } from 'tracked-built-ins';

import { isLinkTo, isLinkToStateSymbol } from 'ember-route-state/utils/link-to.js';
import { deserializeQueryParams, serializeQueryParams, valueMatches } from 'ember-route-state/utils/query-params.js';
import { isSet } from 'ember-route-state/utils/nullish.js';
import { routePaths } from 'ember-route-state/utils/route-paths.js';
import { isRouteInfo } from 'ember-route-state/utils/route-info.js';
import { RouteQueryState } from 'ember-route-state/utils/route-query-state';

export default class RouteStateService extends Service {
  @tracked routeChanged = 0;

  queryState = new RouteQueryState();

  get queryForRoute() {
    const { routeChanged } = this; // consume
    return route => this.queryState.mergeForRoute(route);
  }

  getRoute(name) {
    return getOwner(this).lookup('route:' + name);
  }

  traverseRoute(route, eachFn) {
    if (!route || !eachFn) {
      return;
    }
    let iter;
    if (route.routeInfo) {
      route = route.routeInfo;
    }
    if (typeof route === 'string') {
      const routes = routePaths(route);
      iter = eachRunner => routes.some((name, i) => {
        return eachRunner(name, name);
      });
    } else {
      iter = eachRunner => route.find(routeInfo => eachRunner(routeInfo.name, routeInfo));
    }
    const eachRunner = (name, value) => {
      const route = this.getRoute(name);
      return eachFn(route, name, value);
    };
    return iter(eachRunner);
  }

  transformQueryParams(transformFn, route, queryParams) {
    queryParams = queryParams || route.queryParams;
    const result = {};
    this.traverseRoute(route, route => {
      Object.assign(result, transformFn(route, queryParams));
    });
    return result;
  }

  deserializeAllQueryParams(route, queryParams) {
    return this.transformQueryParams(deserializeQueryParams, route, queryParams);
  }

  deserializeRouteQueryParams(route, queryParams) {
    return deserializeQueryParams(this.getRoute(route), queryParams) || {};
  }

  serializeQueryParams(route, queryParams) {
    return this.transformQueryParams(serializeQueryParams, route, queryParams);
  }

  queryParamsMatch(route, queryParams) {
    const current = this.queryForRoute(route);
    return Object.entries(queryParams || {})
      .every(([ key, value ]) => valueMatches(value, current[key]));
  }

  isActive(routeParams, options) {
    const { routeParams: params, query } = this._linkTo(routeParams, options, false);
    return this._isActive(params, query);
  }

  urlFor(routeParams, options = null) {
    const { route, routeOnlyParams, query }
      = this._linkTo(routeParams, options);
    return this._buildUrl(this._buildUrlParams(route, routeOnlyParams, query));
  }

  transitionTo(routeParams, options = null) {
    return this._buildTransition(this._linkTo(routeParams, options).routeParams);
  }

  /**
   * @typedef {[]|function()} LinkToState Same as routeParams
   * @property {string} url String URL of the route
   * @property {[]} urlParams Params to pass to router.urlFor
   * @property {function} transition Transitions to route
   * @property {function} isActive Whether the route is active
   * @property {function} routeParams Params to pass to router.transitionTo or LinkTo@route
   * @property {string} route Route name
   * @property {[]} models Route models
   * @property {[]} query Query-params hash
   */
  /**
   * @typedef {{}} LinkToOptions
   * @property {boolean} [transition] True to return LinkToState transition
   *   function, otherwise LinkToState routeParams array will be returned.
   * @property {string|[]} [route] Route name or routeParams
   * @property {[]|*} [models] Route models
   * @property {[]|*} [model] Route model
   * @property {{} [query] Query-params hash
   */
  /**
   * @returns {function(string|[], LinkToOptions|null): LinkToState}}
   */
  get linkTo() {
    const { routeChanged } = this; // consume
    return (routeParams, options = {}) => {
      const {
        routeParams: params, routeOnlyParams, urlParams,
        route, models, query,
      } = this._linkTo(routeParams, options);
      return this._buildLinkTo(
        params,
        routeOnlyParams,
        this._buildTransition(params),
        this._buildUrlParams(route, routeOnlyParams, query),
        route,
        models,
        query,
        options,
      );
    };
  }

  isLinkTo(linkTo) {
    return isLinkTo(linkTo);
  }

  @service('router') _router;

  constructor() {
    super(...arguments);

    if (globalThis.Ssr) {
      return;
    }

    const routeChanged = (transition) => {
      ++this.routeChanged;
      transition.to?.find(({ name, queryParams }) => {
        this.queryState.routeChange(name,
          this.deserializeRouteQueryParams(name, queryParams)
        );
      });
    };
    this._router.on('routeDidChange', routeChanged);
  }

  _linkTo(routeParams, options, mergeState = true) {
    let routeOptions = {};
    if (this.isLinkTo(routeParams)) {
      routeParams = routeParams.routeParams;
    } else if (isRouteInfo(routeParams)) {
      routeOptions = {
        route: routeParams.name,
        models: routeParams.paramNames
          .filter(p => routeParams.params[p] !== undefined)
          .map(p => routeParams.params[p]),
        query: routeParams.queryParams,
      };
      routeParams = null;
    } else if (routeParams && typeof routeParams === 'object'
      && !Array.isArray(routeParams)
    ) {
      routeOptions = routeParams;
      routeParams = null;
    }
    options ||= {};
    const routeParamsOptions = this._parseRouteParams(routeParams);
    const routeOptionsOptions = this._parseRouteParams(routeOptions.route);
    const optionsRouteOptions = this._parseRouteParams(options.route);
    let route = ifString(options.route) || optionsRouteOptions.route
      || ifString(routeOptions.route) || routeOptionsOptions.route
      || routeParamsOptions.route || this._router.currentRouteName
    ;
    const models = options.models || arrayIf(options.model)
      || optionsRouteOptions.models || arrayIf(optionsRouteOptions.model)
      || routeOptions.models || arrayIf(routeOptions.model)
      || routeOptionsOptions.models || arrayIf(routeOptionsOptions.model)
      || routeParamsOptions.models || arrayIf(routeParamsOptions.model) || []
    ;
    // Note: these are in the reverse order, as the above, since
    // later values overwrite previous values in this syntax.
    let query = {
      ...this.parseQuery(route, routeParamsOptions.query),
      ...this.parseQuery(route, routeOptionsOptions.query),
      ...this.parseQuery(route, routeOptions.query),
      ...this.parseQuery(route, optionsRouteOptions.query),
      ...this.parseQuery(route, options.query),
    };
    if (mergeState) {
      query = { ...this.queryForRoute(route), ...query };
    }
    const routeOnlyParams = [ ...(route ? [ route ] : []), ...models ];
    routeParams = [ ...routeOnlyParams, { queryParams: query } ];
    return { routeParams, routeOnlyParams, route, models, query };
  }

  _parseRouteParams(routeParams) {
    if (typeof routeParams === 'string') {
      return { route: routeParams };
    }
    if (!Array.isArray(routeParams)) {
      return {};
    }
    if (Array.isArray(routeParams?.[0])) {
      // Allow passing routeParams array as first argument for helpers
      routeParams = routeParams[0];
    }
    let route = null;
    let models = [];
    let query = null;
    let i = 0;
    for (const param of routeParams || []) {
      if (!isSet(param)) {
        continue;
      }
      if (!i++ && typeof param === 'string') {
        route = param;
        continue;
      }
      if (param && typeof param === 'object') {
        query = param;
        break;
      }
      models.push(param);
    }
    return { route, models: route || models.length ? models : null, query };
  }

  parseQuery(route, query) {
    if (!query || typeof query !== 'object') {
      query = null;
    } else if (query?.queryParams) {
      query = this.deserializeAllQueryParams(route, query.queryParams);
    } else if (query?.isQueryParams) {
      query = this.deserializeAllQueryParams(route, query.values || {});
    }
    return query;
  }

  _buildTransition(routeParams) {
    return () => this._router.transitionTo(...routeParams);
  }

  _buildIsActive(routeParams, query) {
    return () => this._isActive(routeParams, query);
  }

  @action _isActive(routeParams, query) {
    return routeParams?.length && routeParams[0]
      && this._router.isActive(...routeParams)
      && this.queryParamsMatch(routeParams, query);
  }

  _buildUrlParams(route, routeOnlyParams, query) {
    return [
      ...routeOnlyParams,
      { queryParams: this.serializeQueryParams(route, query) },
    ];
  }

  _buildUrl(urlParams) {
    return this._router.urlFor(...urlParams);
  }

  _buildLinkTo(routeParams, routeOnlyParams, transition, urlParams, route, models, query, options = {}) {
    return Object.assign(options?.transition ? transition : routeParams, {
      [isLinkToStateSymbol]: true,
      url: this._buildUrl(urlParams),
      urlParams,
      transition,
      isActive: this._buildIsActive(routeParams, query),
      routeParams,
      linkTo: routeParams,
      route,
      models,
      query,
    });
  }
}

function arrayIf(value, defaultValue = null) {
  return value ? [ value ] : defaultValue;
}

function ifString(value, defaultValue = null) {
  return typeof value === 'string' ? value : defaultValue;
}
